import type { ApolloError } from '@apollo/client'
import type { FC } from 'react'
import React, { useState } from 'react'
import styled from 'styled-components'

import H6 from '@/components/H6'
import LoadingSpinner from '@/components/LoadingSpinner'
import SmallPill from '@/components/SmallPill'

interface Props {
  onClick: (notifyApplicant: boolean) => void
  onCancel?: () => void
  loading?: boolean
  error?: ApolloError
  isSuccessful?: boolean
}

const ConfirmationText = styled.em`
  display: block;
  margin-bottom: 20px;
  color: #666;
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
`

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.4;
`

const StyledCheckbox = styled.input`
  margin: 0;
  margin-top: 2px;
  flex-shrink: 0;
`

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: min-content min-content;
  grid-column-gap: 10px;
  justify-content: end;
`

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #ffeaea;
  border-radius: 5px;
  border-left: 4px solid #d32f2f;
`

const CancelRequestDialog: FC<Props> = ({ onClick, loading = false, error }) => {
  const [stage, setStage] = useState<'idle' | 'confirm' | 'success'>('idle')
  const [notifyApplicant, setNotifyApplicant] = useState<boolean>(false)

  const handleConfirm = () => {
    onClick(notifyApplicant)
  }

  const handleCancel = () => {
    setStage('idle')
  }

  return (
    <div>
      {stage === 'idle' && (
        <SmallPill type="button" onClick={() => setStage('confirm')} styleType="danger">
          Cancel request
        </SmallPill>
      )}
      {stage === 'confirm' && (
        <>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <H6>Please confirm?</H6>
              <ConfirmationText>
                Are you sure you would like to cancel this request?
              </ConfirmationText>

              {error && <ErrorMessage>{error.message}</ErrorMessage>}

              <CheckboxContainer>
                <CheckboxLabel>
                  <StyledCheckbox
                    type="checkbox"
                    checked={notifyApplicant}
                    onChange={e => setNotifyApplicant(e.target.checked)}
                    disabled={loading}
                    aria-describedby="notify-description"
                  />
                  <span id="notify-description">
                    Yes and inform the applicant we have cancelled this IDVT request
                  </span>
                </CheckboxLabel>
              </CheckboxContainer>

              <ButtonContainer>
                <SmallPill type="button" onClick={handleCancel} disabled={loading}>
                  Back
                </SmallPill>
                <SmallPill
                  type="button"
                  onClick={handleConfirm}
                  styleType="danger"
                  disabled={loading}
                >
                  Confirm
                </SmallPill>
              </ButtonContainer>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default CancelRequestDialog
